import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'fastingInsulin', 'fastingGlucose', 'glucoseUnitMgdl', 'glucoseUnitMmol',
    'glucoseHint', 'homaIrDisplay', 'categoryContainer', 'categoryDot',
    'categoryName', 'categoryDescription', 'insulinDisplay', 'glucoseDisplay',
    'glucoseUnitDisplay', 'recommendation'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const fastingInsulin = parseFloat(this.fastingInsulinTarget.value) || 0
    const fastingGlucose = parseFloat(this.fastingGlucoseTarget.value) || 0
    const isMmol = this.glucoseUnitMmolTarget.checked

    // Update glucose hint based on unit selection
    if (isMmol) {
      this.glucoseHintTarget.textContent = 'Measured after 8-12 hour fast (typical range: 3.9-5.5 mmol/L)'
      this.glucoseUnitDisplayTarget.textContent = 'mmol/L'
    } else {
      this.glucoseHintTarget.textContent = 'Measured after 8-12 hour fast (typical range: 70-100 mg/dL)'
      this.glucoseUnitDisplayTarget.textContent = 'mg/dL'
    }

    // Update input displays
    this.insulinDisplayTarget.textContent = fastingInsulin > 0 ? fastingInsulin.toFixed(1) : '--'
    this.glucoseDisplayTarget.textContent = fastingGlucose > 0 ? fastingGlucose.toFixed(1) : '--'

    if (fastingInsulin <= 0 || fastingGlucose <= 0) {
      this.resetDisplay()
      return
    }

    // Convert mmol/L to mg/dL if needed (1 mmol/L = 18.0182 mg/dL)
    let glucoseMgdl = fastingGlucose
    if (isMmol) {
      glucoseMgdl = fastingGlucose * 18.0182
    }

    // Calculate HOMA-IR: (Fasting Insulin x Fasting Glucose) / 405
    const homaIr = (fastingInsulin * glucoseMgdl) / 405

    this.homaIrDisplayTarget.textContent = homaIr.toFixed(2)

    // Categorize and update display
    const category = this.getCategory(homaIr)
    this.updateDisplay(category)
  }

  getCategory(homaIr) {
    if (homaIr < 1.0) {
      return {
        name: 'OPTIMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Excellent insulin sensitivity. ' +
          'Your body efficiently uses insulin to regulate blood sugar levels.',
        recommendation: 'Maintain your healthy lifestyle. ' +
          'Continue regular exercise, balanced nutrition, and adequate sleep ' +
          'to preserve optimal metabolic health.'
      }
    }
    if (homaIr < 2.0) {
      return {
        name: 'NORMAL',
        color: 'blue-500',
        borderColor: 'border-blue-500',
        bgColor: 'bg-blue-50',
        description: 'Normal insulin sensitivity. Your body responds appropriately to insulin.',
        recommendation: 'Continue healthy habits. Monitor periodically, especially if you have ' +
          'risk factors like family history of diabetes or weight gain.'
      }
    }
    if (homaIr < 3.0) {
      return {
        name: 'EARLY INSULIN RESISTANCE',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Early signs of insulin resistance detected. ' +
          'Your cells are beginning to respond less effectively to insulin.',
        recommendation: 'Lifestyle intervention is highly effective at this stage. ' +
          'Focus on reducing refined carbohydrates, increasing physical activity, ' +
          'and achieving healthy weight. Recheck in 3-6 months.'
      }
    }
    return {
      name: 'SIGNIFICANT INSULIN RESISTANCE',
      color: 'red-500',
      borderColor: 'border-red-500',
      bgColor: 'bg-red-50',
      description: 'Significant insulin resistance present. ' +
        'Your body requires higher insulin levels to maintain blood sugar control.',
      recommendation: 'Consult with your healthcare provider for comprehensive evaluation. ' +
        'Aggressive lifestyle modification and possibly medication may be indicated. ' +
        'Additional testing (OGTT, HbA1c) recommended.'
    }
  }

  updateDisplay(category) {
    this.categoryDotTarget.className = `w-4 h-4 bg-${category.color}`
    this.categoryNameTarget.textContent = category.name
    this.categoryDescriptionTarget.textContent = category.description
    this.recommendationTarget.textContent = category.recommendation
    this.categoryContainerTarget.className = `p-6 ${category.borderColor} border-2 ${category.bgColor}`
  }

  resetDisplay() {
    this.homaIrDisplayTarget.textContent = '--'
    this.categoryDotTarget.className = 'w-4 h-4 bg-gray-300'
    this.categoryNameTarget.textContent = 'ENTER VALUES'
    this.categoryDescriptionTarget.textContent = 'Enter your fasting insulin and glucose values to calculate your HOMA-IR score.'
    this.recommendationTarget.textContent = 'Enter your values for personalized guidance.'
    this.categoryContainerTarget.className = 'p-6 border-2 border-gray-300 bg-white'
  }
}
